import { useRef, useState } from 'react';
import type { DragEvent } from 'react';
import type { AssetRef } from '@motorcity/shared';
import { uploadAsset } from '../api/client';
import { useFinalizeAsset } from '../api/hooks';
import { getConfig } from '../config';
import { errorMessage, useToast } from './ui/Toast';

const ACCEPTED = 'image/jpeg,image/png,image/webp,image/avif,image/svg+xml';
const MAX_BYTES = 25 * 1024 * 1024;

function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

/**
 * Image slot with drag-and-drop, click-to-browse and a live progress bar.
 *
 * The whole tile is a drop target rather than a small button, because editors
 * arrive here with a folder of press shots open next to the browser.
 */
export function AssetField({
  label,
  hint,
  value,
  prefix,
  onChange,
  required = false,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  value: AssetRef | undefined;
  /** Logical folder inside the bucket, e.g. `brands/mg/models`. */
  prefix: string;
  onChange: (next: AssetRef | undefined) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | undefined>();
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const finalize = useFinalizeAsset();
  const toast = useToast();

  async function handleFile(file: File) {
    setError(undefined);
    if (!file.type.startsWith('image/')) {
      setError('That file is not an image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`${formatBytes(file.size)} exceeds the 25 MB limit.`);
      return;
    }
    try {
      setProgress(0);
      const { key } = await uploadAsset(file, prefix, setProgress);
      // Dimensions and WebP renditions are produced server-side, so the stored
      // reference is complete the moment the editor sees it.
      const asset = await finalize.mutateAsync({ key, contentType: file.type });
      onChange(asset);
      toast.success(`${label} uploaded`);
    } catch (err) {
      const message = errorMessage(err, 'Upload failed');
      setError(message);
      toast.error(message);
    } finally {
      setProgress(undefined);
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  const previewUrl = value ? `${getConfig().contentBaseUrl}/${value.key}` : undefined;
  const busy = progress !== undefined || finalize.isPending;
  const pct = progress !== undefined ? Math.round(progress * 100) : undefined;

  return (
    <div className="field">
      <span className="field-label">
        {label}
        {required ? (
          <span className="required" aria-hidden="true">
            {' *'}
          </span>
        ) : null}
      </span>

      <div
        className={[
          'dropzone',
          dragging ? 'dragging' : '',
          value ? 'filled' : '',
          busy ? 'busy' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        {previewUrl ? (
          <img className="dropzone-preview" src={previewUrl} alt="" loading="lazy" />
        ) : (
          <svg
            className="dropzone-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 16.5V19a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2.5M12 15V3m0 0L7.5 7.5M12 3l4.5 4.5" />
          </svg>
        )}

        <div className="dropzone-body">
          {busy ? (
            <>
              <span className="dropzone-title">
                {finalize.isPending ? 'Generating sizes…' : `Uploading… ${pct}%`}
              </span>
              <progress className="upload-progress" value={progress ?? 1} max={1} />
            </>
          ) : (
            <>
              <span className="dropzone-title">
                {value ? 'Replace image' : 'Drop an image or browse'}
              </span>
              <span className="dropzone-meta">
                {value ? (
                  <>
                    {value.width && value.height ? `${value.width}×${value.height}` : 'Uploaded'}
                    {value.bytes ? ` · ${formatBytes(value.bytes)}` : ''}
                    {value.renditions.length
                      ? ` · ${value.renditions.length} responsive size${value.renditions.length > 1 ? 's' : ''}`
                      : ''}
                  </>
                ) : (
                  'JPG, PNG, WebP, AVIF or SVG · up to 25 MB'
                )}
              </span>
            </>
          )}

          <div className="dropzone-actions">
            <input
              ref={input}
              type="file"
              accept={ACCEPTED}
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => input.current?.click()}
            >
              {value ? 'Replace' : 'Browse'}
            </button>
            {value ? (
              <button
                type="button"
                className="btn subtle danger"
                disabled={busy}
                onClick={() => onChange(undefined)}
              >
                Remove
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {hint && !error ? <span className="field-hint">{hint}</span> : null}
      {error ? <span className="field-error">{error}</span> : null}
    </div>
  );
}
